import { useMemo } from "react";

import {
  type CriteriaFilterValue,
  type Provider,
  ProviderSelectBox,
  V2Button,
} from "@stacklet/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { graphql, useLazyLoadQuery } from "react-relay";

import { HelpDrawerContent } from "app/components/HelpDrawer/HelpDrawerContent";
import { useConfig } from "app/contexts/ConfigContext";

import { RULES_FILTERS_HELP_CONTENT } from "../consts";
import { useResourceMatchCount } from "../hooks/useResourceMatchCount";
import { AccountCriteria } from "./dropdowns/AccountCriteria";
import { BindingCriteria } from "./dropdowns/BindingCriteria";
import { PolicyCriteria } from "./dropdowns/PolicyCriteria";
import { ResourceCriteria } from "./dropdowns/ResourceCriteria";

import type { RulesFiltersQuery } from "./__generated__/RulesFiltersQuery.graphql";
import type { RulesFilterFormData } from "./schema";

type FilterFields = keyof Pick<
  RulesFilterFormData,
  "accountFilters" | "resourceFilters" | "policyFilters" | "bindingFilters"
>;

interface Props {
  onNextStep?: () => void;
  onCancel?: () => void;
}

export function RulesFilters({ onNextStep, onCancel }: Props) {
  const config = useConfig();

  const data = useLazyLoadQuery<RulesFiltersQuery>(
    graphql`
      query RulesFiltersQuery(
        $resourceTagValuesFilter: FilterElementInput
        $resourceCriteriaTagValuesFilter: FilterElementInput
      ) {
        ...AccountCriteria_accountGroups
        ...AccountCriteria_accounts
        ...AccountCriteria_tagKeys
        ...AccountCriteria_tagValues
          @arguments(filter: $resourceTagValuesFilter)
        ...BindingCriteria_bindings
        ...PolicyCriteria_categories
        ...PolicyCriteria_policies
        ...PolicyCriteria_policyCollections
        ...PolicyCriteria_severities
        ...ResourceCriteria_resourceTypes
        ...ResourceCriteria_tagKeys
        ...ResourceCriteria_tagValues
          @arguments(filter: $resourceCriteriaTagValuesFilter)
        ...useResourceMatchCount_query
      }
    `,
    {
      resourceTagValuesFilter: { single: { name: "value", value: "" } },
      resourceCriteriaTagValuesFilter: { single: { name: "value", value: "" } },
    },
  );

  const { control, setValue } = useFormContext<RulesFilterFormData>();

  const providerFilters = useWatch<RulesFilterFormData, "providerFilters">({
    control,
    name: "providerFilters",
  });
  const accountFilters = useWatch<RulesFilterFormData, "accountFilters">({
    control,
    name: "accountFilters",
  });
  const resourceFilters = useWatch<RulesFilterFormData, "resourceFilters">({
    control,
    name: "resourceFilters",
  });
  const policyFilters = useWatch<RulesFilterFormData, "policyFilters">({
    control,
    name: "policyFilters",
  });
  const bindingFilters = useWatch<RulesFilterFormData, "bindingFilters">({
    control,
    name: "bindingFilters",
  });

  const filters = useMemo(
    () => ({
      providerFilters,
      accountFilters,
      resourceFilters,
      policyFilters,
      bindingFilters,
    }),
    [
      providerFilters,
      accountFilters,
      resourceFilters,
      policyFilters,
      bindingFilters,
    ],
  );

  const {
    matchCount,
    isLoading: isCountLoading,
    hasFilters,
  } = useResourceMatchCount(data, filters);

  function handleFilterSelectionsChange(
    filter: FilterFields,
    selections: CriteriaFilterValue[],
  ): void {
    setValue(filter, selections, { shouldDirty: true });
  }

  return (
    <>
      <form className="space-y-lg p-6">
        <Controller
          control={control}
          name="providerFilters"
          render={({ field }) => (
            <div className="mb-lg flex justify-between rounded-xs border border-overlay-medium p-lg">
              <ProviderSelectBox
                className="gap-lg"
                onSelected={(selections) => {
                  if (selections.length === 0) {
                    field.onChange([]);
                    return;
                  }

                  field.onChange([
                    {
                      criteria: "provider",
                      name: "provider",
                      operator: "is-one-of",
                      value: selections.map((provider) => ({
                        id: provider,
                        name: provider,
                      })),
                    },
                  ]);
                }}
                providers={config?.cloud_providers ?? []}
                selectedProviders={(field.value ?? []).flatMap((cv) =>
                  (cv.value ?? []).flatMap((value) =>
                    value?.id ? [value.id as Provider] : [],
                  ),
                )}
              />
              <div className="text-label-small text-text-secondary">
                Provider
              </div>
            </div>
          )}
        />

        <AccountCriteria
          handleSelectionsChange={(selections) =>
            handleFilterSelectionsChange("accountFilters", selections)
          }
          initialCriteriaFilters={accountFilters ?? []}
          refs={{
            accountGroupsRef: data,
            accountsRef: data,
            tagKeysRef: data,
            tagValuesRef: data,
          }}
        />

        <ResourceCriteria
          handleSelectionsChange={(selections) =>
            handleFilterSelectionsChange("resourceFilters", selections)
          }
          initialCriteriaFilters={resourceFilters ?? []}
          refs={{
            resourceTypesRef: data,
            tagKeysRef: data,
            tagValuesRef: data,
          }}
        />

        <PolicyCriteria
          handleSelectionsChange={(selections) =>
            handleFilterSelectionsChange("policyFilters", selections)
          }
          initialCriteriaFilters={policyFilters ?? []}
          refs={{
            categoriesRef: data,
            policiesRef: data,
            policyCollectionsRef: data,
            severitiesRef: data,
          }}
        />

        <BindingCriteria
          bindingsRef={data}
          handleSelectionsChange={(selections) =>
            handleFilterSelectionsChange("bindingFilters", selections)
          }
          initialCriteriaFilters={bindingFilters ?? []}
        />

        <div className="flex items-center justify-end gap-x-2">
          {hasFilters ? (
            <span className="text-label-small text-text-secondary">
              {isCountLoading
                ? "Counting matching resources…"
                : matchCount !== null
                  ? `${matchCount.toLocaleString()} matching resource${matchCount === 1 ? "" : "s"}`
                  : null}
            </span>
          ) : null}
          <V2Button onPress={onCancel} variant="tertiary">
            Cancel
          </V2Button>
          <V2Button
            isDisabled={!hasFilters}
            onPress={onNextStep}
            variant="secondary"
          >
            Continue To Delivery Method
          </V2Button>
        </div>
      </form>
      <HelpDrawerContent content={RULES_FILTERS_HELP_CONTENT} />
    </>
  );
}
